<script setup lang="ts">
import PartTitleHead from '@parts/part-title-head.vue';
import ButtonPrimary from '@buttons/button-primary.vue';
import PartFooterNavigation from '@parts/part-footer-navigation.vue';
import CoreLoader from '@core/core-loader.vue';
import Popup from '@components/popup/popup.vue';
import PartCheckoutAuth from '@parts/part-checkout-auth.vue';
import {computed, onBeforeMount, onBeforeUnmount, onMounted, ref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import {useToast} from 'vue-toast-notification';
import {useNumber} from '@composables/number';
import {useCurrency} from '@composables/currency';
import {usePaymentApi} from '@store/api/paymentApi';
import {useReserveApi} from '@store/api/reserveApi';
import {useBillingApi} from '@store/api/billingApi';
import {usePaymentFlowStore} from '@store/paymentFlow';
import type {Bill, Currency, PaymentInvoice, PaymentProvider, PaymentTariff, PaySystem, ReservePack, SocialProvider} from '@store/api/interfaces';
import {FailedCreateReservePackInvoiceError} from '@store/api/interfaces';
import {invoice as invoiceTG, openLink, openTelegramLink} from '@tma.js/sdk-vue';
import router from '@/router';
import isInTelegram from '@/utils/isInTelegram';
import {gtmAddPaymentInfo, gtmBeginCheckout, gtmClientInfo} from '@/composables/gtm';
import {consumeHandoffCode} from '@composables/handoff';
import {useUserStore} from '@store/user';

const { t, locale } = useI18n();
const route = useRoute();
const toast = useToast();
const { withSpaces } = useNumber();
const paymentApi = usePaymentApi();
const reserveApi = useReserveApi();
const billingApi = useBillingApi();
const paymentFlow = usePaymentFlowStore();
const userStore = useUserStore();

const allTariffs = ref<PaymentTariff[]>([]);
const packs = ref<ReservePack[]>([]);
const providers = ref<PaymentProvider[]>([]);
const isLoading = ref(true);
const loaderText = ref(t('common.loader.provider'));
const selectedProviderCode = ref<string>('');
const createdBill = ref<Bill | null>(null);
const checkBillStatusTimer = ref<ReturnType<typeof setInterval> | null>(null);
const paymentPopup = ref();
// Deliberately not a ref: named popups are reused by the browser, so window.open
// can hand back a window that already navigated to a foreign origin. Vue's ref
// setter probes whatever it stores (isShallow reads __v_isShallow), and reading
// any property of a cross-origin window throws SecurityError.
let paymentWindow: Window | null = null;
// Invoice link shown in the processing popup for link-based providers. Always
// visible: window.open === null only catches the built-in popup blocker, while
// extensions can return a window and silently close it, and a window that did
// open can fail to load — none of that is detectable from JS. A plain anchor
// (user gesture, target=_blank) works in every case.
const fallbackPayLink = ref<string | null>(null);

/**
 * Оплата пакета резервного трафика вместо подписки: `?pack=<id>` со страницы
 * оплаты (вкладка «Пакет ГБ»).
 *
 * Шаг выбора платёжки общий — платёжка одна и та же, — расходятся только предмет
 * счёта, эндпоинт и то, куда возвращать после оплаты. Промокоды и рефералки к
 * пакетам не применяются, анонимной покупки у них нет.
 */
const packId = computed((): number | null => {
	const raw = Number(route.query.pack);
	return raw > 0 ? raw : null;
});

const isPackFlow = computed((): boolean => packId.value !== null);

const selectedPack = computed((): ReservePack | undefined => {
	return packs.value.find(pack => pack.id === packId.value);
});

// Read months & promo from query or store
const selectedMonths = computed((): number => {
	const fromQuery = Number(route.query.months);
	if (fromQuery > 0) return fromQuery;
	return paymentFlow.selectedMonths ?? 1;
});

const isAuthenticated = computed((): boolean => {
	return userStore.loggedIn;
});

// Email for unauthenticated users
const email = ref('');

const isValidEmail = (value: string): boolean => {
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
};

// Provider mapping: each (tariff, provider) pair maps to one or more UI buttons based on
// tariff currency and provider rub_mode. ProviderItem carries tariffId so the chosen item
// fully determines the tariff being paid for (no ambiguity for multi-currency providers).
// Вид кнопки — для порядка на шаге оплаты. Рублёвые «карта или СБП одной
// кнопкой» (rub_mode auto) и старые провайдеры без режима считаются картой.
type PaymentKind = 'sbp' | 'card_rub' | 'card_usd' | 'stars' | 'crypto';

// Порядок кнопок — решение владельца, не зависит от порядка валют в API:
// СБП, карта рублями, зарубежная карта, звёзды, крипта
const PAYMENT_KIND_ORDER: PaymentKind[] = ['sbp', 'card_rub', 'card_usd', 'stars', 'crypto'];

interface ProviderItem {
	code: string;
	tariffId: number;
	label: string;
	icon: string;
	iconAlt?: string;
	apiProvider: string;
	method?: string;
	kind: PaymentKind;
}

// То, за что можно заплатить: тариф или пакет ГБ в одной валюте
interface PayableEntry {
	id: number;
	currency: Currency;
	support_providers: PaymentProvider[];
}

/**
 * Кнопки способов оплаты по списку записей одного товара в разных валютах:
 * рубли — карта и СБП, USDT — крипта, USD — карта, звёзды — только в мини-апе.
 * Один сборщик для тарифов и пакетов: у пакетов теперь тоже по строке на
 * каждую валюту, и выбор способа оплаты должен быть одинаковым.
 */
const buildProviderItems = (entries: PayableEntry[]): ProviderItem[] => {
	const isMiniApp = isInTelegram();
	const items: ProviderItem[] = [];

	// Dedupe RUB methods: if multiple split providers exist (e.g. freekassa + platega both rub_mode='split'),
	// show only one Карта/СБП pair. First provider in the entry's support_providers list wins.
	const seenRubMethods = new Set<'card' | 'sbp' | 'auto'>();
	// Dedupe non-RUB buttons across entries/providers
	const seenKeys = new Set<string>();

	for (const entry of entries) {
		const currency = entry.currency;

		for (const provider of entry.support_providers) {
			if (currency === 'rub') {
				if (provider.rub_mode === 'split') {
					// Legacy freekassa accepts 'card', new platega expects 'card_rub'
					const cardMethod = provider.code === 'freekassa' ? 'card' : 'card_rub';
					if (!seenRubMethods.has('card') && !seenRubMethods.has('auto')) {
						items.push({
							code: `${provider.code}_card`,
							tariffId: entry.id,
							label: t('payment.providers.card_rub'),
							icon: '/images/payment/mir.svg',
							apiProvider: provider.code,
							method: cardMethod,
							kind: 'card_rub'
						});
						seenRubMethods.add('card');
					}
					if (!seenRubMethods.has('sbp') && !seenRubMethods.has('auto')) {
						items.push({
							code: `${provider.code}_sbp`,
							tariffId: entry.id,
							label: t('payment.providers.sbp'),
							icon: '/images/payment/sbp.svg',
							apiProvider: provider.code,
							method: 'sbp',
							kind: 'sbp'
						});
						seenRubMethods.add('sbp');
					}
					continue;
				}
				if (provider.rub_mode === 'auto') {
					if (seenRubMethods.size > 0) continue;
					items.push({
						code: provider.code,
						tariffId: entry.id,
						label: t(`payment.providers.${provider.code}`, provider.code),
						icon: '/images/payment/mir.svg',
						iconAlt: '/images/payment/sbp.svg',
						apiProvider: provider.code,
						kind: 'card_rub'
					});
					seenRubMethods.add('auto');
					continue;
				}
				// Fallback for RUB providers without rub_mode (legacy: yoomoney, yookassa)
				const rubKey = `rub_${provider.code}`;
				if (seenKeys.has(rubKey)) continue;
				items.push({
					code: provider.code,
					tariffId: entry.id,
					label: t(`payment.providers.${provider.code}`, provider.code),
					icon: '/images/payment/mir.svg',
					apiProvider: provider.code,
					kind: 'card_rub'
				});
				seenKeys.add(rubKey);
				continue;
			}

			if (currency === 'usdt' || currency === 'ton') {
				const cryptoKey = `crypto_${provider.code}`;
				if (seenKeys.has(cryptoKey)) continue;
				items.push({
					code: `${provider.code}_crypto`,
					tariffId: entry.id,
					label: t('payment.providers.crypto_pay'),
					icon: '/images/payment/crypto.svg',
					apiProvider: provider.code,
					kind: 'crypto'
				});
				seenKeys.add(cryptoKey);
				continue;
			}

			if (currency === 'usd') {
				const usdKey = `usd_${provider.code}`;
				if (seenKeys.has(usdKey)) continue;
				items.push({
					code: `${provider.code}_usd`,
					tariffId: entry.id,
					label: t('payment.providers.usd_card'),
					icon: '/images/payment/visa.svg',
					iconAlt: '/images/payment/mastercard.svg',
					apiProvider: provider.code,
					kind: 'card_usd'
				});
				seenKeys.add(usdKey);
				continue;
			}

			if (currency === 'xtr') {
				if (!isMiniApp) continue;
				if (seenKeys.has('xtr')) continue;
				items.push({
					code: 'telegram_stars',
					tariffId: entry.id,
					label: t('payment.providers.telegram_stars'),
					icon: '/images/payment/telegram-star.svg',
					apiProvider: 'telegram_stars',
					kind: 'stars'
				});
				seenKeys.add('xtr');
			}
		}
	}

	// Сортировка стабильная: внутри одного вида порядок из API сохраняется
	return items.sort((a, b) => PAYMENT_KIND_ORDER.indexOf(a.kind) - PAYMENT_KIND_ORDER.indexOf(b.kind));
};

// Пакеты того же объёма во всех валютах — как тарифы того же срока:
// на странице оплаты выбирают объём в валюте витрины, валюту и способ — здесь
const packsForGb = computed((): ReservePack[] => {
	const pack = selectedPack.value;
	if (!pack) return [];
	return packs.value.filter(p => p.gb === pack.gb);
});

const providerItems = computed((): ProviderItem[] => {
	if (isPackFlow.value) {
		return buildProviderItems(packsForGb.value.map(p => ({
			id: p.id,
			currency: p.cost.currency,
			support_providers: p.support_providers
		})));
	}

	return buildProviderItems(
		allTariffs.value
			.filter(t => t.months === selectedMonths.value)
			.map(t => ({ id: t.id, currency: t.cost.currency, support_providers: t.support_providers }))
	);
});

const selectedItem = computed((): ProviderItem | undefined => {
	return providerItems.value.find(i => i.code === selectedProviderCode.value);
});

const { getDisplayCurrency } = useCurrency();
const defaultCurrency = computed((): Currency => getDisplayCurrency(locale.value));

// Resolve the actual tariff: either explicitly via selected item's tariffId,
// or fall back to the locale-default currency for the initial summary display.
const resolvedTariff = computed((): PaymentTariff | undefined => {
	if (selectedItem.value) {
		return allTariffs.value.find(t => t.id === selectedItem.value!.tariffId);
	}
	return allTariffs.value.find(t => t.months === selectedMonths.value && t.cost.currency === defaultCurrency.value);
});

const displayTariff = computed((): PaymentTariff | undefined => {
	return resolvedTariff.value;
});

// Пакет, за который платим: по выбранному способу оплаты — его валюты, до
// выбора — тот, что пришёл в адресе (в валюте витрины)
const resolvedPack = computed((): ReservePack | undefined => {
	if (selectedItem.value) {
		return packs.value.find(p => p.id === selectedItem.value!.tariffId) ?? selectedPack.value;
	}
	return selectedPack.value;
});

const summaryGiftMonths = computed((): number | null => {
	const tariff = allTariffs.value.find(t => t.months === selectedMonths.value);
	if (tariff?.promo_code?.type === 'gift_month' && tariff.promo_code.gift_months) {
		return tariff.promo_code.gift_months;
	}
	return null;
});

const subscriptionPeriodText = computed((): string => {
	if (isPackFlow.value) {
		return selectedPack.value
			? t('common.payment.reserve_pack', { n: selectedPack.value.gb })
			: '';
	}

	const gift = summaryGiftMonths.value;
	if (gift) {
		return t('payment.subscription_months_gift', { n: selectedMonths.value, gift });
	}
	return t('payment.subscription_months', { n: selectedMonths.value });
});

// Цена того, что оплачивается: у пакета своя, у подписки — из тарифа
const displayCost = computed(() => {
	if (isPackFlow.value) return resolvedPack.value?.cost ?? null;
	return displayTariff.value?.cost ?? null;
});

const currencySymbol = computed((): string => {
	if (!displayCost.value) return '';
	return t(`common.currency.${displayCost.value.currency}`);
});

const totalPrice = computed((): string => {
	if (!displayCost.value) return '';
	// Price already includes promo discount from backend
	return withSpaces(displayCost.value.amount) + currencySymbol.value;
});

const isPayEnabled = computed((): boolean => {
	if (!selectedProviderCode.value) return false;

	// Пакет покупает только авторизованный: email и промокод к нему не применяются
	if (isPackFlow.value) return !!resolvedPack.value;

	if (!resolvedTariff.value) return false;
	// Unauthenticated: require valid email
	return !(!isAuthenticated.value && !isValidEmail(email.value.trim()));
});

const onSelectProvider = (code: string) => {
	selectedProviderCode.value = code;
};

// Social login from checkout page — popup flow
// Plain variable, not a ref — see paymentWindow above.
let authWindow: Window | null = null;
const authPopup = ref();
// The auth-result message doesn't carry the provider — remember which button
// opened the popup so the GTM login event gets the right method.
const pendingSocialProvider = ref<SocialProvider>('telegram');

const onSocialLogin = (provider: SocialProvider) => {
	pendingSocialProvider.value = provider;
	// Open auth window synchronously (iOS Safari popup blocker)
	const authUrl = `${import.meta.env.VITE_API_BASE}/auth/${provider}/redirect`;
	authWindow = window.open(authUrl, 'auth', 'width=600,height=700');

	// Show popup with loader
	authPopup.value?.open();
	loaderText.value = t('common.loader.auth');
};

// Listen for postMessage from auth callback window
const onAuthMessage = async (event: MessageEvent) => {
	if (event.origin !== window.location.origin) return;
	if (event.data?.type !== 'auth-result') return;

	authPopup.value?.close();

	// The popup forwards the one-time code rather than a session: the code is
	// single-use and the session belongs here, in the window that owns the
	// checkout.
	const authenticated = event.data.success && event.data.handoffCode
		? await consumeHandoffCode(event.data.handoffCode, pendingSocialProvider.value)
		: false;

	if (authenticated) {
		toast.success(t('login.login_success'), { position: 'top' });

		// If promo was entered, apply it now that we're authenticated
		if (paymentFlow.promoCode) {
			try {
        paymentFlow.appliedPromo = await paymentApi.http.applyPromoCode(paymentFlow.promoCode);
			} catch (error) {
				// Promo apply failed — not critical for the login, but a rejected
				// code must go, or the invoice below would be rejected with it
				const code = paymentApi.methods.promoRejectionCode(error);
				if (code) {
					toast.error(t(`errorCode.${code}`), { position: 'top' });
					await forgetRejectedPromo();
				}
			}
		}

		// Clean up promo from localStorage now that user is authenticated
		paymentFlow.clearStorage();

		// Re-fetch tariffs (now with auth, prices may update)
		allTariffs.value = await paymentApi.http.getTariffs(paymentFlow.promoCode || undefined);
	} else {
		toast.error(t('common.toast.login_error'), { position: 'top' });
	}
};


const goBackToTariffs = () => {
	router.push(isPackFlow.value
		? { name: 'Payment', query: { tab: 'packs' } }
		: { name: 'Payment' });
	// TODO: в будущем может потребоваться router.back() для возврата на внешний сайт
	// router.back();
};

const onCancelAuth = () => {
	if (authWindow && !authWindow.closed) {
		authWindow.close();
	}
	authWindow = null;
	authPopup.value?.close();
};

const onAuthPopupClose = () => {
	if (authWindow && !authWindow.closed) {
		authWindow.close();
	}
	authWindow = null;
};

// GA4 gets the real price paid, but only understands ISO currency codes
// (raw 'xtr'/'usdt' pushes are what inflated revenue). USDT is reported as USD
// (1:1 peg). Stars/TON have no ISO code — report the same plan's usdt/usd
// tariff price as USD; without one skip value entirely, a missing value is
// better than a wrongly-converted one.
const gtmMoney = (tariff: PaymentTariff): { value?: number; currency?: string } => {
	const { amount, currency } = tariff.cost;
	if (currency === 'rub') {
		return { value: amount, currency: 'RUB' };
	}
	if (currency === 'usd' || currency === 'usdt') {
		return { value: amount, currency: 'USD' };
	}
	const usdTariff = allTariffs.value.find(t =>
		t.months === tariff.months && (t.cost.currency === 'usdt' || t.cost.currency === 'usd'));
	if (usdTariff) {
		return { value: usdTariff.cost.amount, currency: 'USD' };
	}
	return {};
};

// Plan ids follow the landing's dataLayer contract: plan_1m / plan_3m / ...
const gtmPlan = (tariff: PaymentTariff): string => `plan_${tariff.months}m`;

const gtmPaymentMethod = (item: ProviderItem, tariff: PaymentTariff): string => {
	if (item.apiProvider === 'telegram_stars') return 'stars';
	if (item.apiProvider === 'crypto_pay') return 'crypto';
	if (item.method === 'sbp') return 'sbp';
	if (item.method === 'card' || item.method === 'card_rub') return 'card';
	if (tariff.cost.currency === 'usd') return 'card';
	// rub_mode 'auto' / legacy providers: card vs SBP is chosen on the
	// provider's page, so report the provider code instead of guessing.
	return item.apiProvider;
};

// There is no client-side purchase event: the bot's server-side Measurement
// Protocol call covers 96–100% of payments and reconciles with billing, while
// the browser one reached 12% and produced every duplicate GA4 recorded (GA4
// does not collapse by transaction_id). The invoice carries the buyer's GA
// identifiers instead — see gtmClientInfo() below.

// begin_checkout belongs to the moment the payment-method step is shown, which
// is what the ad-account goal optimises for. Guarded so the SPA cannot emit it
// twice for one visit to the step (re-render, promo re-fetch).
let beginCheckoutReported = false;
const reportBeginCheckout = () => {
	if (beginCheckoutReported) return;
	// Пакеты в эту цель не входят: она оптимизирует покупку подписки, и добавка
	// докупок размыла бы сигнал, на котором учится рекламный кабинет.
	if (isPackFlow.value) return;
	const tariff = displayTariff.value;
	if (!tariff) return;
	beginCheckoutReported = true;
	gtmBeginCheckout({
		plan: gtmPlan(tariff),
		checkoutSource: 'cabinet',
		coupon: paymentFlow.promoCode || undefined,
		...gtmMoney(tariff),
	});
};

// The payment-method step is on screen once loading finished and the methods
// rendered — that is the moment the goal is meant to count.
watch([isLoading, providerItems], () => {
	if (isLoading.value) return;
	if (!providerItems.value.length) return;
	reportBeginCheckout();
}, { immediate: true });

// Убираем отклонённый бэкендом код отовсюду, откуда чекаут подставил бы его
// снова: стор и ?promo= в адресе. Цены после этого вызывающий перечитывает сам.
// Отмену на бэкенде не зовём: отказ пришёл до применения кода, а живую
// активацию (например, из бота) трогать незачем — по ней бэкенд и посчитает цену.
const forgetRejectedPromo = async () => {
	paymentFlow.clearPromo();
	const { promo: _promo, ...query } = route.query;
	await router.replace({ query });
};

const onClickPayHandler = async () => {
	if (!isPayEnabled.value || !selectedItem.value) return;
	if (!isPackFlow.value && !resolvedTariff.value) return;

	const tariff = resolvedTariff.value;
	const item = selectedItem.value;
	const providerCode = item.apiProvider as PaySystem;
	const isMiniApp = isInTelegram();
	const invoiceData: Record<string, any> = {};

	if (item.method) {
		invoiceData.pay_system = item.method;
	}

	// Open window synchronously before async call (iOS Safari popup blocker)
	const needsPopup = !isMiniApp && providerCode !== 'telegram_stars';
	paymentWindow = needsPopup ? window.open('about:blank', 'payment', 'width=600,height=700') : null;
	const popup = paymentWindow;

	fallbackPayLink.value = null;
	paymentPopup.value?.open();
	loaderText.value = t('common.loader.invoice');

	if (tariff) {
		gtmAddPaymentInfo({
			plan: gtmPlan(tariff),
			paymentMethod: gtmPaymentMethod(item, tariff),
			coupon: paymentFlow.promoCode || undefined,
			...gtmMoney(tariff),
		});
	}

	let invoice: PaymentInvoice;
	try {
		const gaInfo = gtmClientInfo();
		// GA-контекст одинаков для обоих счетов: покупку подтверждает бот с
		// сервера, и ему нужен client_id покупателя.
		const gaOptions = {
			gaClientId: gaInfo.clientId,
			gaSessionId: gaInfo.sessionId,
			gaStatus: gaInfo.gaStatus,
			gaSessionAgeSec: gaInfo.sessionAgeSec,
			gtmLoaded: gaInfo.gtmLoaded,
			capturedAt: gaInfo.capturedAt,
			source: gaInfo.source,
			pageLocation: gaInfo.pageLocation
		};

		invoice = isPackFlow.value
			? await reserveApi.http.packInvoice(resolvedPack.value!.id, providerCode, invoiceData, gaOptions)
			: await paymentApi.http.invoice(
				tariff!.id,
				providerCode,
				invoiceData,
				{
					email: !isAuthenticated.value ? email.value.trim().toLowerCase() : undefined,
					promoCode: paymentFlow.promoCode || undefined,
					...gaOptions
				}
			);
	} catch (error) {
		if (popup) popup.close();
		paymentPopup.value?.close();

		// У пакета условия могли измениться на сервере, пока страница была
		// открыта: 409 — нет оплаченной подписки, 403 — продажи выключили.
		if (isPackFlow.value) {
			const code = error instanceof FailedCreateReservePackInvoiceError ? error.code : undefined;
			toast.error(
				code ? t(`errorCode.${code}`, t('payment.error.create_invoice')) : t('payment.error.create_invoice'),
				{ position: 'top' }
			);
			// Без оплаченной подписки пакет не продать: ведём сразу к тарифам,
			// а не оставляем на чекауте с кнопкой, которая не сработает.
			// `reserve_not_active` — тот же случай на бэкенде до переименования.
			if (code === 'subscription_not_active' || code === 'reserve_not_active') {
				void router.push({ name: 'Payment' });
			}
			return;
		}

		// Промокод бэкенд проверяет ещё раз при выставлении счёта и может
		// отклонить, хотя тарифы с ним только что посчитал. Это ожидаемый отказ,
		// а не сбой: объясняем, сбрасываем код и даём оплатить по актуальной
		// цене. В Sentry такое не отправляем.
		const code = paymentApi.methods.promoRejectionCode(error);
		if (code) {
			toast.error(t('payment.error.promo_rejected', { reason: t(`errorCode.${code}`) }), { position: 'top' });
			await forgetRejectedPromo();
			allTariffs.value = await paymentApi.http.getTariffs();
			return;
		}

		toast.error(t('payment.error.create_invoice'), { position: 'top' });
		throw error;
	}

	createdBill.value = invoice.bill;
	loaderText.value = t('common.loader.invoice_wait_paid');

	switch (providerCode) {
		case 'telegram_stars':
			if (isMiniApp) {
				if (invoiceTG.openUrl.isAvailable()) {
					invoiceTG.openUrl(invoice.link).then((status) => {
						if (status === 'paid') {
							void router.push({ path: '/' });
						}
						paymentPopup.value?.close();
					});
				} else {
					paymentPopup.value?.close();
				}
			}
			break;
		case 'yookassa_web_app':
		case 'freekassa':
		case 'platega':
			fallbackPayLink.value = invoice.link;
			if (isMiniApp) {
				openLink.ifAvailable(invoice.link);
			} else if (popup) {
				popup.location.href = invoice.link;
			}
			break;
		case 'crypto_pay':
			fallbackPayLink.value = invoice.link;
			if (isMiniApp) {
				openTelegramLink.ifAvailable(invoice.link);
			} else if (popup) {
				popup.location.href = invoice.link;
			}
			break;
	}

	// Poll bill status. The HMAC status_token allows sessionless polling — the
	// authed billing endpoint is unreachable for buyers who paid without signing
	// in to the cabinet, whose payment used to go unconfirmed on the client (no
	// redirect). Fall back to the authed endpoint for older backends.
	const statusToken = invoice.status_token;
	billingApi.methods.resetGetBillErrors();
	checkBillStatusTimer.value = setInterval(async () => {
		if (createdBill.value) {
			const bill = statusToken
				? await paymentApi.http.getInvoiceStatus(createdBill.value.id, statusToken)
				: await billingApi.http.get(createdBill.value.id);
			if (bill?.status === 'paid') {
				clearInterval(checkBillStatusTimer.value!);
				if (paymentWindow && !paymentWindow.closed) {
					paymentWindow.close();
				}
				paymentWindow = null;
				paymentPopup.value?.close();
				paymentFlow.reset();
				// Пакет докупают из кабинета — возвращаем туда, где виден баланс.
				// ?paid=1 нужен, чтобы там показать подтверждение: попап оплаты
				// закрывается вместе с этой страницей и сказать об успехе не успевает.
				if (isPackFlow.value) {
					window.location.href = '/account/reserve?paid=1';
					return;
				}
				// The buyer paid without signing in to the cabinet — send them
				// to login with their email prefilled (auto-sends the OTP code).
				window.location.href = isAuthenticated.value
					? '/'
					: `/login?email=${encodeURIComponent(email.value.trim().toLowerCase())}`;
			}
		}
	}, 3000);
};

const cleanupPayment = () => {
	if (checkBillStatusTimer.value) clearInterval(checkBillStatusTimer.value);
	checkBillStatusTimer.value = null;
	if (paymentWindow && !paymentWindow.closed) {
		paymentWindow.close();
	}
	paymentWindow = null;
	fallbackPayLink.value = null;
};

const onCancelPayHandler = () => {
	cleanupPayment();
	paymentPopup.value?.close();
};

// Called when popup is closed by any means (cross, backdrop, button)
const onPaymentPopupClose = () => {
	cleanupPayment();
};

onBeforeMount(async () => {
	isLoading.value = true;

	if (isPackFlow.value) {
		// Анонимной покупки у пакетов нет — счёт выставляется по токену
		if (!isAuthenticated.value) {
			await router.replace({ name: 'Login' });
			return;
		}

		packs.value = await reserveApi.http.getPacks().catch(() => []);

		// Пакет выключили или продажи резерва отключены, пока страница открывалась
		if (!selectedPack.value) {
			await router.replace({ name: 'Payment' });
			return;
		}

		isLoading.value = false;
		return;
	}

	const promoFromQuery = route.query.promo as string | undefined;
	// Unauthenticated users: a promo in the URL takes precedence and overwrites
	// the previously stored one (e.g. coming back from the site with a new promo).
	// Authenticated users keep their stored/applied promo.
	const promoCode = isAuthenticated.value
		? (paymentFlow.promoCode || promoFromQuery)
		: (promoFromQuery || paymentFlow.promoCode);

	const [providersData, tariffsData] = await Promise.all([
		paymentApi.http.getProviders(),
		paymentApi.http.getTariffs(promoCode || undefined)
	]);

	providers.value = providersData;
	allTariffs.value = tariffsData;

	// Check if months is valid
	const hasMonths = tariffsData.some(t => t.months === selectedMonths.value);
	if (!hasMonths) {
		await router.replace({name: 'Payment'});
		return;
	}

	// Store the resolved promo, overwriting any previously stored one
	// (relevant for unauthenticated users arriving with a new promo in the URL).
	if (promoCode && promoCode !== paymentFlow.promoCode) {
		paymentFlow.promoCode = promoCode;
		paymentFlow.appliedPromo = null;
		// For authenticated users: also apply via API
		if (isAuthenticated.value) {
			try {
        paymentFlow.appliedPromo = await paymentApi.http.applyPromoCode(promoCode);
				toast.success(t('payment.promo_applied'), { position: 'top' });
			} catch (error) {
				const code = paymentApi.methods.promoRejectionCode(error);
				if (code) {
					// The tariffs above were priced with the code — re-price without it
					toast.error(t(`errorCode.${code}`), { position: 'top' });
					await forgetRejectedPromo();
					allTariffs.value = await paymentApi.http.getTariffs();
				} else {
					toast.error(t('payment.promo_invalid'), { position: 'top' });
				}
			}
		}
		// Otherwise tariffs are already fetched with promo_code param above
	}

	isLoading.value = false;
});

onMounted(() => {
	window.addEventListener('message', onAuthMessage);
});

onBeforeUnmount(() => {
	if (checkBillStatusTimer.value) {
		clearInterval(checkBillStatusTimer.value);
	}
	window.removeEventListener('message', onAuthMessage);
});
</script>

<template>
	<section class="section-checkout">
		<div class="wrapper section-checkout__wrapper">
			<transition
				appear
				name="fade"
				style="--animation-delay: 0.1s"
			>
				<part-title-head
					icon="/images/wallet-icon.svg"
					:title="isPackFlow ? t('reserve.buy') : t('payment.step2_title')"
					class="section-checkout__content"
				/>
			</transition>

			<template v-if="isLoading">
				<transition
					appear
					name="fade"
					style="--animation-delay: 0.3s"
				>
					<core-loader
						:title="loaderText"
						class="section-checkout__loader"
					/>
				</transition>
			</template>

			<template v-else>
				<!-- Email & social auth (unauthenticated only) -->
				<transition
					v-if="!isAuthenticated"
					appear
					name="fade"
					style="--animation-delay: 0.3s"
				>
					<part-checkout-auth
						v-model:email="email"
						@social-login="onSocialLogin"
					/>
				</transition>

				<!-- Payment method -->
				<transition
					appear
					name="fade"
					:style="`--animation-delay: ${isAuthenticated ? '0.3s' : '0.5s'}`"
				>
					<div class="section-checkout__methods">
						<h3 class="section-checkout__section-title">
							{{ t('payment.payment_method') }}
						</h3>
						<div class="section-checkout__providers">
							<div
								v-for="item in providerItems"
								:key="item.code"
								class="section-checkout__provider"
								:class="{ 'section-checkout__provider_selected': selectedProviderCode === item.code }"
								@click="onSelectProvider(item.code)"
							>
								<div class="section-checkout__provider-left">
									<div class="section-checkout__provider-icons">
										<img :src="item.icon" alt="" class="section-checkout__provider-icon" />
										<img v-if="item.iconAlt" :src="item.iconAlt" alt="" class="section-checkout__provider-icon" />
									</div>
									<span class="section-checkout__provider-label">{{ item.label }}</span>
								</div>
								<div
									class="section-checkout__radio"
									:class="{ 'section-checkout__radio_checked': selectedProviderCode === item.code }"
								>
									<svg v-if="selectedProviderCode === item.code" width="20" height="20" viewBox="0 0 20 20" fill="none">
										<circle cx="10" cy="10" r="10" style="fill: var(--color-purple)"/>
										<path d="M6 10L9 13L14 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
									</svg>
								</div>
							</div>
						</div>
					</div>
				</transition>

				<!-- Your choice -->
				<transition
					appear
					name="fade"
					style="--animation-delay: 0.5s"
				>
					<div class="section-checkout__choice">
						<h3 class="section-checkout__section-title">
							{{ t('payment.your_choice') }}
						</h3>
						<div class="section-checkout__summary-card">
							<div class="section-checkout__summary-left">
								<img src="/images/logo.svg" alt="" class="section-checkout__summary-logo" />
								<div class="section-checkout__summary-info">
									<span class="section-checkout__summary-name">{{ t('common.title') }}</span>
									<span class="section-checkout__summary-period">
										{{ subscriptionPeriodText }}
									</span>
								</div>
							</div>
							<span class="section-checkout__summary-price">{{ totalPrice }}</span>
						</div>
						<!-- Гарантия возврата — условие подписки; на израсходованный
							 трафик она не распространяется, обещать её нельзя -->
						<p v-if="!isPackFlow" class="section-checkout__guarantee">
							{{ t('payment.guarantee') }}
						</p>
					</div>
				</transition>

				<!-- Footer -->
				<transition
					appear
					name="fade"
					style="--animation-delay: 0.7s"
				>
					<part-footer-navigation
						show-back-button
						disable-back-button-action
						@back-button="goBackToTariffs"
						class="section-checkout__footer"
					>
						<button-primary
							:disabled="!isPayEnabled"
							:icon="{ name: 'arrowRight', viewBox: '0 0 40 40' }"
							@click="onClickPayHandler"
						>
							{{ t('payment.pay') }}
						</button-primary>
					</part-footer-navigation>
				</transition>
			</template>
		</div>

		<!-- Payment processing popup -->
		<popup ref="paymentPopup" @close="onPaymentPopupClose">
			<core-loader
				:title="loaderText"
				title-font="font-16-pro-regular"
				class="popup__loader"
			/>
			<p v-if="fallbackPayLink" class="popup__pay-fallback">
				<span class="popup__pay-fallback-hint">{{ t('payment.pay_window_hint') }}</span>
				<a
					:href="fallbackPayLink"
					target="_blank"
					rel="noopener"
					class="popup__pay-fallback-link"
				>{{ t('payment.pay_window_open') }}</a>
			</p>
			<div class="popup__buttons">
				<button
					class="popup__btn popup__btn--primary"
					@click="onCancelPayHandler"
				>
					{{ t('common.buttons.cancel') }}
				</button>
			</div>
		</popup>

		<!-- Auth processing popup -->
		<popup ref="authPopup" @close="onAuthPopupClose">
			<core-loader
				:title="loaderText"
				class="popup__loader"
			/>
			<div class="popup__buttons">
				<button
					class="popup__btn popup__btn--secondary"
					@click="onCancelAuth"
				>
					{{ t('common.buttons.cancel') }}
				</button>
			</div>
		</popup>
	</section>
</template>

<style lang="scss">
.section-checkout {
	padding: 0 0 1.6rem;
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;

	&__wrapper {
		flex: 1 1 auto;
		display: flex;
		flex-direction: column;
	}

	&__content {
		margin-bottom: 3.2rem;

		@include min-desktop {
			margin-bottom: 4.8rem;
		}
	}

	&__loader {
		margin-bottom: 3.2rem;

		@include min-desktop {
			margin-bottom: 6.4rem;
		}
	}

	&__section-title {
		@include font-24-pro-bold;
		margin-bottom: 1.6rem;

		@include max-desktop {
			font-size: 2.1rem;
		}
	}

	&__methods {
		margin-bottom: 3.2rem;
	}

	&__providers {
		display: flex;
		flex-direction: column;
		gap: 1rem;
	}

	&__provider {
		display: flex;
		align-items: center;
		justify-content: space-between;
		height: 5.2rem;
		padding: 0 1.6rem;
		border-radius: 1.6rem;
		background: rgba(var(--color-active-rgb), 0.06);
		border: 1px solid rgba(var(--color-active-rgb), 0.12);
		cursor: pointer;
		transition: all 0.2s ease;

		@include min-desktop {
			height: 6.2rem;
		}

		&_selected {
			border-color: rgba(var(--color-active-rgb), 0.25);
		}
	}

	&__provider-left {
		display: flex;
		align-items: center;
		gap: 1.6rem;
	}

	&__provider-icons {
		display: flex;
		align-items: center;
		gap: 0.6rem;
	}

	&__provider-icon {
		height: 2.1rem;
		width: auto;
	}

	&__provider-label {
		@include font-regular;

		@include min-desktop {
			@include font-18-pro-medium;
		}
	}

	&__radio {
		width: 2rem;
		height: 2rem;
		border-radius: 50%;
		border: 1px solid rgba(115, 93, 207, 0.12);
		background: #fff;
		display: flex;
		align-items: center;
		justify-content: center;

		@include theme-dark {
			background: rgba(255, 255, 255, 0.08);
			border-color: rgba(255, 255, 255, 0.12);
		}

		&_checked {
			border: none;
			background: none;
		}
	}

	&__choice {
		margin-bottom: 3.2rem;

		@include min-desktop {
			margin-bottom: 4.8rem;
		}
	}

	&__summary-card {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding: 1.6rem;
		border-radius: 1.6rem;
		background: rgba(var(--color-active-rgb), 0.06);
		border: 1px solid rgba(var(--color-active-rgb), 0.12);
		margin-bottom: 1.6rem;
	}

	&__summary-left {
		display: flex;
		align-items: center;
		gap: 1.6rem;
	}

	&__summary-logo {
		width: 4.2rem;
		height: 4.2rem;
		border-radius: 50%;

		@include min-desktop {
			width: 4.8rem;
			height: 4.8rem;
		}
	}

	&__summary-info {
		display: flex;
		flex-direction: column;
		gap: 0.6rem;
	}

	&__summary-name {
		@include font-16-pro-regular;

		@include min-desktop {
			@include font-18-pro-medium;
		}
	}

	&__summary-period {
		@include font-regular;
		opacity: 0.7;
	}

	&__summary-price {
		@include font-18-pro-medium;
	}

	&__guarantee {
		@include font-12-pro-regular;
		opacity: 0.5;

		@include min-desktop {
			@include font-16-pro-regular;
		}
	}

	&__footer {
		margin-top: auto;
	}
}

// A quiet rescue line, not a competing CTA: the popup's job is "we're waiting
// for the payment" (loader title, 1.6rem), the manual link sits a step below it.
.popup__pay-fallback {
	@include font-regular;
	line-height: 1.4;
	width: 100%;
	margin-bottom: 2rem;
	text-align: center;

	&-hint {
		opacity: 0.6;
	}

	&-link {
		// Vue condenses the inter-element whitespace away — restore the gap
		margin-left: 0.5rem;
		color: var(--color-purple);
		white-space: nowrap;
		text-decoration: underline;
		text-underline-offset: 0.3rem;

		@include theme-dark {
			// The gradient's light stop — #735DCF lacks contrast on the dark popup
			color: #8F7AE6;
		}
	}
}
</style>
